import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useFormik } from 'formik';
import ApprovalList from '../../components/approval/ApprovalList';

import ApprStatusChip from '../../components/common/datagrid/ApprStatusChip';

import { listApprovals } from '../../modules/approvalModule/approvals';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import * as yup from 'yup';
import dayjs from 'dayjs';
import ApprovalListBox from '../../components/approval/ApprovalListBox';
import ApprovalStatusList from '../../components/approval/ApprovalStatusList';

const ApprovalListContainer = () => {
  const dispatch = useDispatch();
  let navigate = useNavigate();

  const [searchType, setSearchType] = useState('M');
  const [searchStatus, setSearchStatus] = useState('S');
  const [searchDocTyp, setSearchDocTyp] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('docCd');
  const [searchWords, setSearchWords] = useState(null);
  const [searchStrtDt, setSearchStrtDt] = useState('');
  const [searchEndDt, setSearchEndDt] = useState('');

  const { approvals, error, loading } = useSelector(
    ({ approvalModule, loading }) => ({
      approvals: approvalModule.approvals.approvals,
      error: approvalModule.approvals.error,
      loading: loading['approval/LIST_APPROVALS'],
    }),
  );

  const columns = useMemo(() => {
    // 1. 기본 컬럼 정의
    const baseColumns = [
      {
        field: 'id',
        headerName: 'ID',
        hide: true,
        type: 'string',
        editable: false,
        sortable: false,
        disableExport: true,
      },
      {
        field: 'apprUuid',
        hide: true,
        type: 'string',
        editable: false,
        sortable: false,
        disableExport: true,
      },
      {
        field: 'docuUuid',
        hide: true,
        type: 'string',
        editable: false,
        sortable: false,
        disableExport: true,
      },
      {
        field: 'docuCd',
        hide: true,
        type: 'string',
        editable: false,
        sortable: false,
        disableExport: true,
      },
      {
        field: 'docuTyp',
        hide: true,
        type: 'string',
        editable: false,
        sortable: false,
        disableExport: true,
      },
      {
        field: 'docuNo',
        headerName: '문서번호',
        minWidth: 120,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
    ];

    // 2. reqPlanDt 값이 하나라도 존재하는지 확인
    const isTaxDoc = approvals?.some((row) => row.docuTyp === '세금');

    // 3. 데이터가 있을 때만 매출일자 컬럼 추가
    if (isTaxDoc) {
      baseColumns.push({
        field: 'reqPlanDt',
        headerName: '매출일자(계산서 작성 일자)',
        renderHeader: () => (
          <div style={{ lineHeight: '1.2rem', textAlign: 'center' }}>
            매출일자<br />(계산서 작성 일자)
          </div>
        ),
        minWidth: 120,
        flex: 1,
        type: 'date',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (params) => params.value ? dayjs(params.value).format('YYYY-MM-DD') : '',
      });
    }

    // 4. 나머지 컬럼 결합
    return [
      ...baseColumns,
      {
        field: 'partnerInc',
        headerName: isTaxDoc ? '매출처' : '고객명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
      },
      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 300,
        flex: 1,
        type: 'string',
        editable: false,
      },
      {
        field: 'docuNow',
        headerName: '결재 단계',
        minWidth: 100,
        flex: 0.5,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => `${params.row.docuNow} / ${params.row.docuCnt}`,
      },
      {
        field: 'myApprStage', // 중복 필드명 방지를 위해 수정
        headerName: '내 결재 단계',
        minWidth: 100,
        flex: 0.5,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'docuNowUsr',
        headerName: '현재 결재자',
        minWidth: 120,
        flex: 0.6,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'DOCU_STATUS',
        headerName: '결재여부',
        minWidth: 150,
        flex: 0.7,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ApprStatusChip,
      },
      {
        field: 'billCrtNm',
        headerName: '작성자',
        minWidth: 80,
        flex: 0.3,
        type: 'string',
        editable: false,
      },
      {
        field: 'crtDt',
        headerName: isTaxDoc ? '발행 요청일' : '작성일',
        minWidth: 150,
        flex: 0.8,
        type: 'string',
        editable: false,
      },
    ];
  }, [approvals]); // approvals 데이터가 갱신될 때마다 컬럼 필터링 재실행

  const handleRowClick = (param, event) => {
    navigate(`/approvalmng/${param.row.docuUuid}`);
  };

  const searchList = (values) => {
    const startDate =
      values.searchStrtDt !== null && values.searchStrtDt !== ''
        ? dayjs(values.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      values.searchEndDt !== null && values.searchEndDt !== ''
        ? dayjs(values.searchEndDt).format('YYYY-MM-DD')
        : '';

    let searchData = {
      searchStatus: searchStatus,
      searchDocTyp: searchDocTyp,
      [values.searchKeyword]: values.searchWords,
      strtDt: startDate,
      endDt: endDate,
    };

    setSearchKeyword(values.searchKeyword);
    setSearchWords(values.searchWords);
    setSearchStrtDt(values.searchStrtDt);
    setSearchEndDt(values.searchEndDt);

    dispatch(listApprovals(searchType, searchData));
  };

  const initialValues = {
    searchKeyword: searchKeyword,
    searchWords: searchWords,
    searchStrtDt: searchStrtDt,
    searchEndDt: searchEndDt,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('검색 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      setTimeout(() => {
        searchList(values);
        setSubmitting(false);
      }, 1000);
    },
  });

  const onChangeStatus = (stats) => {
    if (stats === searchStatus) stats = '';
    setSearchStatus(stats);
  };

  const onChangeDocTyp = (type) => {
    if (type === searchDocTyp) type = '';
    setSearchDocTyp(type);
  };

  const onChangeTyp = (type) => {
    if (type === searchType) type = 'M';
    setSearchType(type);
  };

  useEffect(() => {
    searchList(formik.values);
  }, [searchStatus, searchDocTyp, searchType]); // searchType 등 의존성 추가

  return (
    <>
      <BreadcrumbsContainer title="결재관리/결재 리스트" />
      <ApprovalListBox formik={formik} />
      <ApprovalStatusList
        searchStatus={searchStatus}
        onChangeStatus={onChangeStatus}
        searchDocTyp={searchDocTyp}
        onChangeDocTyp={onChangeDocTyp}
        searchType={searchType}
        onChangeTyp={onChangeTyp}
      />
      <ApprovalList
        columns={columns}
        rows={approvals}
        loading={loading}
        handleRowClick={handleRowClick}
      />
    </>
  );
};

export default ApprovalListContainer;
